import React, { useState , useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  InputAdornment,
  Paper,
} from "@mui/material";
import { VisibilityOff, Visibility } from "@mui/icons-material";
import { CToast, CToastBody } from "@coreui/react";
import { UserType } from "../../service/Enums";
import { API_URL } from "../../utils/constants";
import axios from "axios";
import jwtInterceptor from "../../service/jwtInterceptor";

export const FinalisationInscriptionAgenceAdmin = () => {
  const INITIAL_STATE = {
    username: "",
    firstname: "",
    email: "",
    password: "",
    name: "",
    personalEmail: "",
    phoneNumber: "",
    birthDate: "",
    address: "",
    agencyName: "",
    siret: "",
    agencyAddress: "",
    description: ""
  };

  const location = useLocation();
  const navigate = useNavigate();
  const [form, setForm] = useState(INITIAL_STATE);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [toast, addToast] = useState(0);

  // Validations
  const isEmailValid = /^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/.test(form.personalEmail || "");
  const isPhoneValid = !form.phoneNumber || /^[0-9+]{10,}$/.test(form.phoneNumber);
  const isSiretValid = !form.siret || /^[0-9]{14}$/.test(form.siret);


  
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await jwtInterceptor.get(`${API_URL}/user/agencies-users`);
        const users = response.data?.data?.results || [];
  

        const userData = users.find(user => user.userDetails?.typeUser === "agence_admin");
  
        if (userData) {
          setForm(prevForm => ({
            ...prevForm,
            username: userData._source.username || "",
            firstname: userData._source.firstname || "",
            email: userData._source.email || "",
            name: userData._source.name || "",
            agencyName: userData.userDetails?.agence || "",
            password: userData.password || ""
          }));
        } else {
          console.warn("Aucun utilisateur agence_admin trouvé.");
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des données:", error);
      } finally {
        setLoading(false);
      }
    };
  
    fetchUserData();
  }, []);
  

  
  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!form.siret) {
      newErrors.siret = "Veuillez entrer le numéro SIRET.";
    } else if (!isSiretValid) {
      newErrors.siret = "Le numéro SIRET doit contenir 14 chiffres.";
    }

    if (!form.agencyAddress) {
      newErrors.agencyAddress = "Veuillez entrer l'adresse de l'agence.";
    }

    if (form.personalEmail && !isEmailValid) {
      newErrors.personalEmail = "Veuillez entrer une adresse e-mail valide.";
    }

    if (form.phoneNumber && !isPhoneValid) {
      newErrors.phoneNumber = "Veuillez entrer un numéro de téléphone valide.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    const payload = {
      userInformation: {
        ...form,
        username: form.username.toLowerCase(),
      },
      type: UserType.AGENCE_ADMIN.value,
      newAgence: {
        nom: form.agencyName,
        siret: form.siret,
        address: form.agencyAddress,
        description: form.description
      },
    };

    try {
      const res = await axios.post(`${API_URL}/register/finalize`, payload);
      if (!res.data.error) {
        addToast(
          <CToast title="Bootstrap React">
            <CToastBody>
              <p className="text-green">
                Inscription finalisée avec succès!
              </p>
            </CToastBody>
          </CToast>
        );
        // Nettoyer les données d'inscription du localStorage
        localStorage.removeItem('registrationData');
        
        setTimeout(() => {
          navigate("/");
        }, 1000);
      } else {
        setErrors((prev) => ({
          ...prev,
          submit: res.data.message || "Une erreur est survenue.",
        }));
      }
    } catch (err) {
      setErrors((prev) => ({
        ...prev,
        submit: err.response?.data?.message || "Une erreur est survenue.",
      }));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box className="my-4 px-4 text-center">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="my-4 px-4">
      <Paper className="py-4 px-4 rounded shadow-none">
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            "& .boxfield": {
              maxWidth: {
                xl: "49%",
                lg: "49%",
                md: "49%",
                sm: "49%",
                xs: "100%",
              },
              width: "100%",
            },
          }}
        >
          <h4 className="mb-3 mt-3 fw-bold">Information de connexion :</h4>
          <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap" }}>
            <Box className="boxfield">
              <TextField
                fullWidth
                name="email"
                label="E-mail *"
                value={form.email}
                disabled
                sx={{ bgcolor: "#f5f5f5" }}
              />
            </Box>
            <Box className="boxfield">
              <TextField
                fullWidth
                name="username"
                label="Pseudo *"
                value={form.username}
                disabled
                sx={{ bgcolor: "#f5f5f5" }}
              />
            </Box>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap" }}>
            <Box className="boxfield">
              <TextField
                fullWidth
                name="password"
                label="Mot de passe *"
                type={showPassword ? "text" : "password"}
                value={form.password}
                onChange={handleChange}
                error={Boolean(errors.password)}
                helperText={errors.password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ minWidth: "unset" }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>

          <h4 className="mb-3 mt-3 fw-bold">Information personnelle :</h4>
          <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap" }}>
            <Box className="boxfield">
              <TextField
                fullWidth
                name="name"
                label="Nom *"
                value={form.name}
                onChange={handleChange}
                error={Boolean(errors.name)}
                helperText={errors.name}
              />
            </Box>
            <Box className="boxfield">
              <TextField
                fullWidth
                name="firstname"
                label="Prénom *"
                value={form.firstname}
                disabled
                sx={{ bgcolor: "#f5f5f5" }}
              />
            </Box>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap" }}>
            <Box className="boxfield">
              <TextField
                fullWidth
                name="personalEmail"
                label="E-mail personnel"
                value={form.personalEmail}
                onChange={handleChange}
                error={Boolean(errors.personalEmail)}
                helperText={errors.personalEmail}
              />
            </Box>
            <Box className="boxfield">
              <TextField
                fullWidth
                name="phoneNumber"
                label="Numéro de téléphone"
                value={form.phoneNumber}
                onChange={handleChange}
                error={Boolean(errors.phoneNumber)}
                helperText={errors.phoneNumber}
              />
            </Box>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap" }}>
            <Box className="boxfield">
              <TextField
                fullWidth
                name="birthDate"
                label="Date de naissance"
                type="date"
                value={form.birthDate}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box className="boxfield">
              <TextField
                fullWidth
                name="address"
                label="Adresse"
                value={form.address}
                onChange={handleChange}
              />
            </Box>
          </Box>

          <h4 className="mb-3 mt-3 fw-bold">Information Agence :</h4>
          <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap" }}>
            <Box className="boxfield">
              <TextField
                fullWidth
                name="agencyName"
                label="Nom de l'agence *"
                value={form.agencyName}
                disabled
                sx={{ bgcolor: "#f5f5f5" }}
              />
            </Box>
            <Box className="boxfield">
              <TextField
                fullWidth
                name="siret"
                label="Siret *"
                value={form.siret}
                onChange={handleChange}
                error={Boolean(errors.siret)}
                helperText={errors.siret}
              />
            </Box>
          </Box>
          <TextField
            fullWidth
            name="agencyAddress"
            label="Adresse de l'agence *"
            value={form.agencyAddress}
            onChange={handleChange}
            error={Boolean(errors.agencyAddress)}
            helperText={errors.agencyAddress}
          />
          <TextField
            fullWidth
            name="description"
            label="Description"
            value={form.description}
            onChange={handleChange}
            multiline
            rows={4}
          />

          {errors.submit && (
            <Box sx={{ color: "error.main", mt: 1 }}>{errors.submit}</Box>
          )}

          <Button
            fullWidth
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{
              mt: 2,
              height: "40px",
              bgcolor: "#299CDB",
              "&:hover": {
                bgcolor: "#1b8ac7",
              },
            }}
          >
            {loading ? <CircularProgress size={24} /> : "Enregistrer"}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};